import React, { useState, useCallback, useRef, useEffect, useContext } from 'react';
import { Input, Text, TooltipTrigger, AutoComplete, DropdownMenu, Spinner, sparkToast, Button, SparkIcon } from '@spark';
import '@/features/datasets/components/sparkSelectFullWidth.css';
import { data_from_server_post } from '@/shared/http/client';
import { MAX_LABELS_TO_DISPLAY, getLabelColumnPluralLabel, isGenerativeTask, getGenerativeOutputNoun } from '@/shared/domain/taskTypes';
import { TaskType } from '@/shared/domain/taskTypes';
import LabelExtractionManager from '@/features/datasets/components/LabelExtractionManager';
import { buildTaskLabelMatcher } from '@/features/datasets/utils/labelFuzzyMatching';
import EmptyResponsesChoice from '@/features/datasets/components/datasetUpload/EmptyResponsesChoice';
import { shouldShowEmptyResponsesChoice } from '@/features/datasets/utils/emptyResponsesHandling';
import { useAuth } from '@/features/auth/AuthContext';
import { GlobalContext } from '@/app/providers/globalState';
import { initTwoPhaseProgressItem, markTwoPhaseUploadCompleteStartProcessing } from '@/features/datasets/utils/uploadProgress';

// Custom debounce hook
const useDebounce = (callback, delay) => {
    const timeoutRef = useRef(null);

    return useCallback((...args) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => callback(...args), delay);
    }, [callback, delay]);
};

/** Inline replacement for the deleted ColumnSelector design-system component. */
const InlineColumnSelector = ({ label, value, onChange, options = [], placeholder = "Select column", loading = false, error, title, tooltipText }) => {
    const [open, setOpen] = useState(false);
    const headingText = title || ((label === "train" || label === "test") ? `Select ${label} split` : `Select ${label} column`);
    const tooltip = tooltipText || (label === "labels" ? "Select the column that contains the labels for the examples" : "Select the column that contains the examples");
    const normalizedOptions = (options || []).map((opt) => {
        if (typeof opt === 'string') return { value: opt, label: opt };
        const optValue = opt?.value ?? '';
        return { value: optValue, label: opt?.label ?? String(optValue) };
    });
    const selectedOption = normalizedOptions.find((o) => o.value === value);
    const items = normalizedOptions.map((o) => ({
        label: String(o.label),
        state: o.value === value ? 'selected' : 'default',
        onClick: () => {
            onChange(o.value);
            setOpen(false);
        },
    }));
    return (
        <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                <Text variant="base-regular" style={{ fontWeight: 400, color: '#222', fontSize: '14px', margin: 0 }}>{headingText}</Text>
                <TooltipTrigger text={tooltip}>
                    <span style={{ color: '#878787', cursor: 'help', display: 'inline-flex' }}>
                        <SparkIcon slug="circle-help" size="small" />
                    </span>
                </TooltipTrigger>
                {loading ? <Spinner size="small" /> : null}
            </div>
            <DropdownMenu
                selectType="single-select"
                size="medium"
                showLabel={false}
                placeholder={loading ? 'Loading...' : placeholder}
                text={selectedOption ? String(selectedOption.label) : undefined}
                disabled={loading}
                open={open}
                onOpenChange={setOpen}
                items={items}
                className="spark-select-full-width"
            />
            {error ? <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 6 }}>{error}</div> : null}
        </div>
    );
};


// Component for Review Extracted Labels section
const ReviewExtractedLabels = ({
    datasetId,
    columns,
    loading,
    selectedLabelColumn,
    selectedTextColumn,
    onLabelColumnChange,
    onTextColumnChange,
    labelError,
    selectedTaskType,
    isMultilabel,
    delimiter,
    setDelimiter
}) => {
    if (!datasetId) {
        return null;
    }

    const isSummarization = isGenerativeTask(selectedTaskType);
    const sectionTitle = isSummarization
        ? "Map Dataset Columns"
        : "Review Extracted Labels and Examples";

    return (
        <div style={{ marginTop: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '32px', marginBottom: '16px' }}>
                <Text variant="base-medium" style={{ margin: 0, fontWeight: 500, color: '#262626', fontSize: '20px' }} className="fs-unmask">
                    {sectionTitle}
                </Text>
            </div>
            <InlineColumnSelector
                label="example"
                value={selectedTextColumn}
                onChange={onTextColumnChange}
                options={columns}
                placeholder="Select example column"
                loading={loading}
            />
            <InlineColumnSelector
                label={isSummarization ? getGenerativeOutputNoun(selectedTaskType) : "labels"}
                value={selectedLabelColumn}
                onChange={onLabelColumnChange}
                options={columns}
                placeholder={
                    isSummarization
                        ? `Select ${getGenerativeOutputNoun(selectedTaskType)} column`
                        : "Select labels column"
                }
                loading={loading}
                error={labelError}
            />
            {selectedTaskType === TaskType.classification && isMultilabel && selectedLabelColumn && (
                <InlineColumnSelector
                    label="labels"
                    title="Select labels delimiter"
                    value={delimiter}
                    onChange={setDelimiter}
                    options={[
                        { value: ';', label: 'Semicolon ;' },
                        { value: ',', label: 'Comma ,' },
                        { value: ' ', label: 'Space ␣' },
                        { value: '|', label: 'Pipe |' },
                        { value: '\\t', label: 'Tab \\t' },
                        { value: 'list', label: 'List []' }
                    ]}
                    placeholder="Delimiter"
                />
            )}
        </div>
    );
};

const HuggingFaceInput = ({ value, onChange, onDatasetSelect, placeholder = "Search or paste dataset", task_type }) => {
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [searchOpen, setSearchOpen] = useState(false);
    const { role } = useAuth();
    // Search function. Goes through the backend (`/search-hf-datasets`) so the
    // caller's Hugging Face token is attached server-side and never reaches
    // the browser.
    const performSearch = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setOptions([]);
            return;
        }

        setLoading(true);
        try {
            let requestError = null;
            const result = await data_from_server_post(
                'search-hf-datasets',
                { search: searchTerm, limit: 10 },
                null,
                (message) => {
                    requestError = new Error(String(message || 'Hugging Face search failed'));
                }
            );
            if (requestError) {
                throw requestError;
            }
            const datasets = Array.isArray(result?.datasets) ? result.datasets : [];

            // Spark AutoComplete items render plain-string labels, so the option
            // label is the dataset id (description/downloads sub-text dropped).
            const datasetOptions = datasets
                .filter((dataset) => dataset && dataset.id)
                .map((dataset) => ({
                    value: dataset.id,
                    label: dataset.id,
                }));

            setOptions(datasetOptions);
        } catch (error) {
            console.error('Error fetching datasets:', error);
            sparkToast.error('Failed to fetch datasets from Hugging Face');
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    // Debounced search function
    const debouncedSearch = useDebounce(performSearch, 300);

    // Handle search input changes
    const handleSearch = (searchValue) => {
        debouncedSearch(searchValue);
    };

    // Handle selection from autocomplete
    const handleSelect = (selectedValue) => {
        onChange(selectedValue);
        onDatasetSelect(selectedValue); // Trigger column fetching only on selection
    };

    // Handle input changes (both typing and selection)
    const handleInputChange = (inputValue) => {
        onChange(inputValue);
        // Don't trigger column fetching on typing, only on selection
    };

    const supportedTaskTypes = [TaskType.classification, TaskType.summarization, TaskType.contentGeneration];
    if (!supportedTaskTypes.includes(task_type)) {
        return (
            <div
                style={{
                    width: '100%',
                    textAlign: 'center',
                    background: '#fafafa',
                    border: '1px dashed #d9d9d9',
                    borderRadius: 8,
                    padding: 24,
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#878787', display: 'inline-flex' }}>
                        <SparkIcon slug="clock-hour-icon" size="medium" />
                    </span>
                    <div style={{ fontWeight: 500, color: '#222' }}>Coming soon</div>
                    <div style={{ color: '#666', fontSize: 12 }}>
                        Hugging Face datasets for this task will be available shortly.
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div style={{ width: '100%' }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '12px',
                gap: '8px'
            }}>
                <Text variant="base-regular" style={{
                    fontWeight: 400,
                    color: '#222',
                    fontSize: '16px',
                    margin: 0
                }}>
                    Hugging face datasets
                </Text>
                <TooltipTrigger text="Search for datasets from Hugging Face or paste a dataset URL">
                    <span style={{ color: '#878787', cursor: 'help', display: 'inline-flex' }}>
                        <SparkIcon slug="circle-help" size="small" />
                    </span>
                </TooltipTrigger>
                {loading ? <Spinner size="small" /> : null}
            </div>
            <AutoComplete
                type="default"
                size="medium"
                placeholder={placeholder}
                open={searchOpen}
                onOpenChange={setSearchOpen}
                value={value}
                items={options.map((opt) => ({
                    label: String(opt.label ?? opt.value),
                    onClick: () => {
                        handleSelect(opt.value);
                        setSearchOpen(false);
                    },
                }))}
                emptyText={loading ? 'Loading...' : 'No results'}
                disabled={role === 'Viewer'}
                onChange={(e) => {
                    handleInputChange(e.target.value);
                    handleSearch(e.target.value);
                }}
                className="spark-select-full-width"
            />
        </div>
    );
};

const HuggingFaceTab = ({
    currentUsecase,
    handleUploadDatasetSuccess,
    handleUploadBenchmarkSuccess,
    selectedTaskType,
    onUploadSuccess,
    setIsDataAnalysisLoading,
    onHuggingFaceDataChange,
    huggingFaceData,
    /** When set, confirm only returns dataset + splits — Data Collection maps columns. */
    onPick,
}) => {
    const selectionOnly = typeof onPick === 'function';
    const { setProgressStatus, clearCancelledUploadSuppression } = useContext(GlobalContext);
    const [datasetValue, setDatasetValue] = useState('');
    const [selectedDatasetId, setSelectedDatasetId] = useState(null);
    const [columns, setColumns] = useState([]);
    const [columnsLoading, setColumnsLoading] = useState(false);
    const [selectedLabelColumn, setSelectedLabelColumn] = useState(null);
    const [selectedTextColumn, setSelectedTextColumn] = useState(null);
    const [extractedLabels, setExtractedLabels] = useState([]);
    const [labelsLoading, setLabelsLoading] = useState(false);
    const [labelMappings, setLabelMappings] = useState({});
    // Classification: how to handle empty label rows. Multilabel:
    // 'none_selected' -> [""] (default), 'unlabeled' -> [], 'exclude' -> skip.
    // Single-label: 'unlabeled' -> [] (default) or 'exclude' -> skip.
    const [emptyMultilabelHandling, setEmptyMultilabelHandling] = useState('none_selected');
    const [uploading, setUploading] = useState(false);
    const [labelError, setLabelError] = useState('');
    const [labelLimitSamples, setLabelLimitSamples] = useState([]);
    const [submissionError, setSubmissionError] = useState('');
    const [splitValidationError, setSplitValidationError] = useState('');
    const [hasAttemptedSubmission, setHasAttemptedSubmission] = useState(false);
    const [availableSplits, setAvailableSplits] = useState([]);
    const [splitsLoading, setSplitsLoading] = useState(false);
    const [selectedTrainSplit, setSelectedTrainSplit] = useState(null);
    const [selectedTestSplit, setSelectedTestSplit] = useState(null);
    const [delimiter, setDelimiter] = useState(null);

    // Wrapper functions to clear split validation error when splits are selected
    const handleTrainSplitChange = (value) => {
        setSelectedTrainSplit(value);
        setSplitValidationError(''); // Clear error when user selects a split
    };

    const handleTestSplitChange = (value) => {
        setSelectedTestSplit(value);
        setSplitValidationError(''); // Clear error when user selects a split
    };

    // Update submission error function (only after first submission attempt)
    const updateSubmissionError = React.useCallback(() => {
        // Only update error message if user has already attempted submission
        if (!hasAttemptedSubmission) {
            return;
        }

        // Check if any final labels (after replacement) contain dots
        const finalLabels = extractedLabels.map(label => {
            const replacement = labelMappings[label];
            return replacement && replacement !== 'none' ? replacement : label;
        });

        const labelsWithDots = finalLabels.filter(label => label && label.includes('.'));
        if (labelsWithDots.length > 0) {
            setSubmissionError(`Labels cannot contain "." character. Please replace labels: ${labelsWithDots.join(', ')}`);
        } else {
            setSubmissionError('');
            setSplitValidationError('');
        }
    }, [extractedLabels, labelMappings, hasAttemptedSubmission]);

    // Update submission error when labels or mappings change
    React.useEffect(() => {
        if (extractedLabels.length > 0 && Object.keys(labelMappings).length > 0 && submissionError) {
            updateSubmissionError();
        }
    }, [extractedLabels, labelMappings, updateSubmissionError]);

    // Fetch dataset columns when datasetId changes
    React.useEffect(() => {
        if (!selectedDatasetId) {
            setColumns([]);
            setColumnsLoading(false);
            setSelectedLabelColumn(null);
            setSelectedTextColumn(null);
            setExtractedLabels([]);
            setLabelsLoading(false);
            setLabelMappings({});
            setLabelError('');
            setLabelLimitSamples([]);
            setSubmissionError('');
            setSplitValidationError('');
            setHasAttemptedSubmission(false);
            return;
        }

        // Create-task Data Collection maps columns on the step, not here.
        if (selectionOnly) {
            setColumns([]);
            setColumnsLoading(false);
            return;
        }

        const fetchDatasetColumns = async () => {
            setColumnsLoading(true);

            data_from_server_post(
                "get-hf-dataset-columns",
                {
                    dataset_id: selectedDatasetId,
                    client_id: currentUsecase?.client_id
                },
                (response) => {
                    if (response.success && response.columns) {
                        setColumns(response.columns);

                        // Auto-select "text" column as example column if available
                        if (!selectionOnly && response.columns.includes('text')) {
                            setSelectedTextColumn('text');
                        }
                    } else {
                        sparkToast.error(response.error || 'Failed to fetch dataset columns');
                    }
                    setColumnsLoading(false);
                },
                (error) => {
                    console.error('Error fetching dataset columns:', error);
                    sparkToast.error('Failed to fetch dataset columns from Hugging Face: ' + error);
                    setColumnsLoading(false);
                }
            );
        };

        fetchDatasetColumns();
    }, [selectedDatasetId, selectionOnly]);

    // Fetch extracted labels when label column is selected; if multilabel classification, wait for delimiter.
    // Summarization has no discrete labels — skip label fetching entirely.
    React.useEffect(() => {
        if (selectionOnly || !selectedLabelColumn || !selectedDatasetId) {
            setExtractedLabels([]);
            setLabelMappings({});
            setLabelsLoading(false);
            setLabelError('');
            setLabelLimitSamples([]);
            return;
        }

        if (isGenerativeTask(selectedTaskType)) {
            setExtractedLabels([]);
            setLabelMappings({});
            setLabelsLoading(false);
            setLabelError('');
            setLabelLimitSamples([]);
            return;
        }

        const needsDelimiter = (selectedTaskType === TaskType.classification) && (currentUsecase?.is_multilabel === true);
        if (needsDelimiter && !delimiter) {
            return;
        }

        const fetchExtractedLabels = async () => {
            setLabelsLoading(true);
            setExtractedLabels([]);
            setLabelMappings({});
            setLabelError('');

            data_from_server_post(
                "get-hf-dataset-labels",
                {
                    dataset_id: selectedDatasetId,
                    label_column: selectedLabelColumn,
                    client_id: currentUsecase?.client_id,
                    is_multilabel: currentUsecase?.is_multilabel === true && selectedTaskType === TaskType.classification,
                    delimiter: needsDelimiter ? delimiter : undefined
                },
                (response) => {
                    const totalFromBackend = response?.total_label_count;
                    const exceededLimit = totalFromBackend && totalFromBackend > MAX_LABELS_TO_DISPLAY;

                    if (exceededLimit) {
                        const sampleValues = Array.isArray(response.labels) ? response.labels : [];
                        const pluralLabel = getLabelColumnPluralLabel(selectedTaskType);
                        setExtractedLabels([]);
                        setLabelMappings({});
                        setLabelLimitSamples(sampleValues);
                        setLabelError(
                            `Found ${totalFromBackend} unique ${pluralLabel} in the selected column. ` +
                            `Maximum allowed is ${MAX_LABELS_TO_DISPLAY}. ` +
                            `This usually means the wrong column was selected.`
                        );
                        setLabelsLoading(false);
                        return;
                    }

                    if (response.success && response.labels) {
                        setExtractedLabels(response.labels);

                        // Prefill "Replace or create" like the file-upload flow:
                        // map each extracted label onto a near-identical task
                        // label (case / separators / plural) when one exists.
                        const taskLabels =
                            selectedTaskType === TaskType.NER
                                ? currentUsecase?.manually_added_entities
                                : currentUsecase?.manually_added_labels;
                        const matchTaskLabel = buildTaskLabelMatcher(taskLabels);
                        setLabelMappings(
                            Object.fromEntries(
                                response.labels.map((label) => [
                                    label,
                                    matchTaskLabel(label) ?? label,
                                ])
                            )
                        );
                        setLabelError('');
                        setLabelLimitSamples([]);
                    } else {
                        setExtractedLabels([]);
                        setLabelMappings({});
                        setLabelLimitSamples([]);
                        setLabelError(response.error || 'Failed to fetch labels. Choose a different label column.');
                    }
                    setLabelsLoading(false);
                },
                (error) => {
                    console.error('Error fetching extracted labels:', error);
                    setExtractedLabels([]);
                    setLabelMappings({});
                    setLabelError('Failed to fetch extracted labels from dataset');
                    setLabelsLoading(false);
                }
            );
        };

        fetchExtractedLabels();
    }, [selectionOnly, selectedLabelColumn, selectedDatasetId, delimiter, selectedTaskType, currentUsecase?.is_multilabel]);

    React.useEffect(() => {
        if (onHuggingFaceDataChange) {
            const isSumm = isGenerativeTask(selectedTaskType);
            const hasExtractedLabels = isSumm
                ? (selectedLabelColumn && selectedTextColumn)
                : (extractedLabels.length > 0 && selectedLabelColumn && selectedTextColumn);
            onHuggingFaceDataChange({
                hasExtractedLabels: !!hasExtractedLabels,
                datasetId: selectedDatasetId,
                labelColumn: selectedLabelColumn,
                textColumn: selectedTextColumn,
                extractedLabels,
                labelMappings
            });
        }
    }, [extractedLabels, selectedLabelColumn, selectedTextColumn, selectedDatasetId, labelMappings, selectedTaskType]);

    // Fetch available splits when datasetId changes
    React.useEffect(() => {
        if (!selectedDatasetId) {
            setAvailableSplits([]);
            setSplitsLoading(false);
            setSelectedTrainSplit(null);
            setSelectedTestSplit(null);
            return;
        }

        const fetchDatasetSplits = async () => {
            setSplitsLoading(true);

            data_from_server_post(
                "get-hf-dataset-splits",
                {
                    dataset_id: selectedDatasetId,
                    client_id: currentUsecase?.client_id
                },
                (response) => {
                    if (response.success && response.splits) {
                        setAvailableSplits(response.splits);

                        // Auto-select train and test splits if they contain the keywords
                        const trainSplit = response.splits.find(split =>
                            split.toLowerCase().includes('train')
                        );
                        const testSplit = response.splits.find(split =>
                            split.toLowerCase().includes('test')
                        );

                        setSelectedTrainSplit(trainSplit || null);
                        setSelectedTestSplit(testSplit || null);
                    } else {
                        sparkToast.error(response.error || 'Failed to fetch dataset splits');
                    }
                    setSplitsLoading(false);
                },
                (error) => {
                    console.error('Error fetching dataset splits:', error);
                    sparkToast.error('Failed to fetch dataset splits from Hugging Face');
                    setSplitsLoading(false);
                }
            );
        };

        fetchDatasetSplits();
    }, [selectedDatasetId]);

    // Whether the selected label/output column holds empty values anywhere in
    // the selected splits. Unsettled (scan in flight) must not show the radios —
    // that flashes them and then hides them when the column has none.
    // After settle: true → show; false → hide; null (unverifiable) → show.
    const [hasEmptyValues, setHasEmptyValues] = useState(null);
    const [emptyValuesCheckSettled, setEmptyValuesCheckSettled] = useState(false);
    const emptyValuesRequestIdRef = useRef(0);
    useEffect(() => {
        setHasEmptyValues(null);
        setEmptyValuesCheckSettled(false);
        if (selectionOnly || !selectedDatasetId || !selectedLabelColumn) return;
        const requestId = ++emptyValuesRequestIdRef.current;
        const isMultilabelClassification =
            currentUsecase?.is_multilabel === true && selectedTaskType === TaskType.classification;
        data_from_server_post(
            'get-hf-dataset-empty-values',
            {
                dataset_id: selectedDatasetId,
                column: selectedLabelColumn,
                client_id: currentUsecase?.client_id,
                is_multilabel: isMultilabelClassification,
                delimiter: isMultilabelClassification ? delimiter : undefined,
                splits: [selectedTrainSplit, selectedTestSplit].filter(Boolean),
            },
            (response) => {
                if (emptyValuesRequestIdRef.current !== requestId) return;
                setHasEmptyValues(response?.success ? (response.has_empty_values ?? null) : null);
                setEmptyValuesCheckSettled(true);
            },
            () => {
                if (emptyValuesRequestIdRef.current !== requestId) return;
                setHasEmptyValues(null);
                setEmptyValuesCheckSettled(true);
            }
        );
    }, [
        selectedDatasetId,
        selectedLabelColumn,
        delimiter,
        selectedTrainSplit,
        selectedTestSplit,
        selectedTaskType,
        currentUsecase?.is_multilabel,
        currentUsecase?.client_id,
        selectionOnly,
    ]);

    const showEmptyResponsesChoice = shouldShowEmptyResponsesChoice({
        hasLabelColumn: Boolean(selectedLabelColumn),
        emptyValuesCheckSettled,
        hasEmptyValues,
    });

    const handleDatasetChange = (newValue) => {
        setDatasetValue(newValue);
        setSelectedDatasetId(null);
        setColumns([]);
        setColumnsLoading(false);
        setSelectedLabelColumn(null);
        setSelectedTextColumn(null);
        setExtractedLabels([]);
        setLabelsLoading(false);
        setLabelMappings({});
        setLabelError('');
        setLabelLimitSamples([]);
        setSubmissionError('');
        setHasAttemptedSubmission(false);
    };

    const handleDatasetSelect = (selectedDatasetId) => {
        setSelectedDatasetId(selectedDatasetId);
        setSplitsLoading(true);
        setSelectedLabelColumn(null);
        setSelectedTextColumn(null);
        setExtractedLabels([]);
        setLabelsLoading(false);
        setLabelMappings({});
        setLabelError('');
        setLabelLimitSamples([]);
        setSubmissionError('');
        setHasAttemptedSubmission(false);
    };

    const handleLabelColumnChange = (value) => {
        setSelectedLabelColumn(value);
        setLabelError('');
        setLabelLimitSamples([]);
        setHasEmptyValues(null);
        setEmptyValuesCheckSettled(false);
    };

    const handleTextColumnChange = (value) => {
        setSelectedTextColumn(value);
    };

    const handleDeleteLabel = (labelToDelete) => {
        setExtractedLabels(prev => prev.filter(label => label !== labelToDelete));
        setLabelMappings(prev => {
            const newMappings = { ...prev };
            delete newMappings[labelToDelete];
            return newMappings;
        });
    };

    const handleReplaceLabel = (oldLabel, newLabel) => {
        setLabelMappings(prev => ({
            ...prev,
            [oldLabel]: newLabel
        }));
        // Update submission error based on remaining labels with dots
        updateSubmissionError();
    };

    const handleAddNewLabel = (newLabel) => {
        setLabelMappings(prev => ({
            ...prev,
            [newLabel]: newLabel
        }));
        // Update submission error based on remaining labels with dots
        updateSubmissionError();
    };

    const isSummarization = isGenerativeTask(selectedTaskType);

    const handleHuggingFacePick = () => {
        setHasAttemptedSubmission(true);
        if (!selectedDatasetId) {
            sparkToast.error('Please select a Hugging Face dataset');
            return;
        }
        if (!selectedTrainSplit && !selectedTestSplit) {
            setSplitValidationError(
                'Please specify at least one split (train or test) before adding'
            );
            return;
        }
        setSplitValidationError('');
        onPick({
            datasetId: selectedDatasetId,
            trainSplit: selectedTrainSplit,
            testSplit: selectedTestSplit,
            columns,
        });
    };

    const handleHuggingFaceSubmit = async () => {
        setHasAttemptedSubmission(true);

        const baseFieldsMissing = !selectedDatasetId || !selectedLabelColumn || !selectedTextColumn;
        const classificationFieldsMissing = !isSummarization && (extractedLabels.length === 0 && !labelError);

        if (baseFieldsMissing || classificationFieldsMissing) {
            sparkToast.error('Please complete all required fields before uploading');
            return;
        }

        if (!selectedTrainSplit && !selectedTestSplit) {
            setSplitValidationError('Please specify at least one split (train or test) before uploading');
            return;
        }

        if (!isSummarization) {
            const finalLabels = extractedLabels.map(label => {
                const replacement = labelMappings[label];
                return replacement && replacement !== 'none' ? replacement : label;
            });

            const labelsWithDots = finalLabels.filter(label => label && label.includes('.'));
            if (labelsWithDots.length > 0) {
                setSubmissionError(`Labels cannot contain "." character. Please replace labels: ${labelsWithDots.join(', ')}`);
                return;
            }
        }

        setSubmissionError('');
        setUploading(true);

        try {
            const uploadData = {
                dataset_id: selectedDatasetId,
                label_column: selectedLabelColumn,
                text_column: selectedTextColumn,
                label_mappings: isSummarization ? {} : labelMappings,
                client_id: currentUsecase?.client_id || "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75",
                is_benchmark: false,
                task_type: selectedTaskType || TaskType.classification,
                usecase_id: currentUsecase?._id,
                data_creation_type: "huggingface",
                train_split: selectedTrainSplit,
                test_split: selectedTestSplit,
                is_multilabel: !isSummarization && currentUsecase?.is_multilabel === true && selectedTaskType === TaskType.classification,
                delimiter: isSummarization ? null : delimiter,
                empty_multilabel_handling: emptyMultilabelHandling,
                rejection_label_name: null,
            };

            await data_from_server_post(
                'upload-huggingface-dataset',
                uploadData,
                (result) => {
                    if (result.success) {
                        sparkToast.success('Dataset upload started successfully');

                        // The backend returns clone names (e.g. "CogComp/trec__t_d3d484db")
                        // that the task is actually associated with.  Use them as
                        // progress keys so WebSocket updates match the UI.
                        const trainDatasetKey = result.dataset_name || selectedDatasetId;
                        const benchmarkDatasetKey = result.benchmark_name || (selectedDatasetId ? `${selectedDatasetId}_test` : null);

                        const ucDatasets = Array.isArray(currentUsecase?.datasets) ? currentUsecase.datasets : [];
                        const ucBenchmarks = Array.isArray(currentUsecase?.benchmarks) ? currentUsecase.benchmarks : [];
                        const trainExistedOnUsecase = Boolean(
                            trainDatasetKey && ucDatasets.includes(trainDatasetKey)
                        );
                        const benchmarkExistedOnUsecase = Boolean(
                            benchmarkDatasetKey && ucBenchmarks.includes(benchmarkDatasetKey)
                        );

                        // New upload must not inherit WebSocket suppression from a prior cancel of the same key.
                        if (clearCancelledUploadSuppression) {
                            if (trainDatasetKey) clearCancelledUploadSuppression(trainDatasetKey);
                            if (benchmarkDatasetKey) clearCancelledUploadSuppression(benchmarkDatasetKey);
                        }

                        if (setProgressStatus && selectedTrainSplit && trainDatasetKey) {
                            initTwoPhaseProgressItem({
                                setProgressStatus,
                                key: trainDatasetKey,
                                datasetName: trainDatasetKey,
                                isBenchmark: false,
                                usecaseId: currentUsecase?._id,
                                message: 'Processing Hugging Face dataset',
                                existedOnUsecaseBeforeUpload: trainExistedOnUsecase,
                            });
                            markTwoPhaseUploadCompleteStartProcessing({
                                setProgressStatus,
                                key: trainDatasetKey,
                            });
                        }
                        if (setProgressStatus && selectedTestSplit && benchmarkDatasetKey) {
                            initTwoPhaseProgressItem({
                                setProgressStatus,
                                key: benchmarkDatasetKey,
                                datasetName: benchmarkDatasetKey,
                                isBenchmark: true,
                                usecaseId: currentUsecase?._id,
                                message: 'Processing Hugging Face benchmark',
                                existedOnUsecaseBeforeUpload: benchmarkExistedOnUsecase,
                            });
                            markTwoPhaseUploadCompleteStartProcessing({
                                setProgressStatus,
                                key: benchmarkDatasetKey,
                            });
                        }

                        if (handleUploadDatasetSuccess) {
                            handleUploadDatasetSuccess(trainDatasetKey || selectedDatasetId);
                        }

                        if (onUploadSuccess) {
                            onUploadSuccess();
                        }

                        if (setIsDataAnalysisLoading && selectedTrainSplit) {
                            setIsDataAnalysisLoading(true);
                        }

                        setDatasetValue('');
                        setSelectedDatasetId(null);
                        setColumns([]);
                        setSelectedLabelColumn(null);
                        setSelectedTextColumn(null);
                        setExtractedLabels([]);
                        setLabelMappings({});
                        setLabelLimitSamples([]);
                        setSubmissionError('');
                        setSplitValidationError('');
                        setHasAttemptedSubmission(false);
                    } else {
                        sparkToast.error(result.error || 'Upload failed');
                    }
                },
                (error) => {
                    sparkToast.error(`Upload failed: ${error}`);
                }
            );
        } catch (error) {
            console.error('Error uploading Hugging Face dataset:', error);
            sparkToast.error('Network error occurred during upload');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div style={{
            maxWidth: '600px',
            margin: '0 auto'
        }}>
            <HuggingFaceInput
                value={datasetValue}
                onChange={handleDatasetChange}
                onDatasetSelect={handleDatasetSelect}
                task_type={selectedTaskType}
            />

            {/* Split Selection Dropdowns */}
            {selectedDatasetId && availableSplits.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                            <InlineColumnSelector label="train" value={selectedTrainSplit} onChange={handleTrainSplitChange} options={availableSplits} placeholder="Choose train column" loading={splitsLoading} />
                        </div>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                            <InlineColumnSelector label="test" value={selectedTestSplit} onChange={handleTestSplitChange} options={availableSplits} placeholder="Choose test column" loading={splitsLoading} />
                        </div>
                    </div>
                </div>
            )}

            {!selectionOnly && (
            <ReviewExtractedLabels
                datasetId={selectedDatasetId}
                columns={columns}
                loading={columnsLoading}
                selectedLabelColumn={selectedLabelColumn}
                selectedTextColumn={selectedTextColumn}
                onLabelColumnChange={handleLabelColumnChange}
                onTextColumnChange={handleTextColumnChange}
                labelError={labelError}
                selectedTaskType={selectedTaskType}
                isMultilabel={currentUsecase?.is_multilabel}
                delimiter={delimiter}
                setDelimiter={setDelimiter}
            />
            )}

            {!selectionOnly && !isSummarization && (
                labelLimitSamples.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
                        {labelLimitSamples.map((sample, idx) => (
                            <Input
                                key={idx}
                                value={sample}
                                disabled
                                showLabel={false}
                                style={{ color: '#595959' }}
                            />
                        ))}
                        <Text variant="detail-regular" style={{ color: '#8c8c8c', fontSize: '12px' }}>
                            ...and more
                        </Text>
                    </div>
                ) : (
                    <>
                        <LabelExtractionManager
                            extractedLabels={extractedLabels}
                            loading={labelsLoading}
                            labelMappings={labelMappings}
                            onDeleteLabel={handleDeleteLabel}
                            onReplaceLabel={handleReplaceLabel}
                            onAddNewLabel={handleAddNewLabel}
                            taskType={selectedTaskType}
                            isMultilabel={currentUsecase?.is_multilabel === true}
                        />
                        {showEmptyResponsesChoice && (
                            <EmptyResponsesChoice
                                isMultilabel={currentUsecase?.is_multilabel === true}
                                taskType={selectedTaskType}
                                value={emptyMultilabelHandling}
                                onChange={setEmptyMultilabelHandling}
                                includeGenerateWithAi={false}
                            />
                        )}
                    </>
                )
            )}
            {!selectionOnly && isSummarization && showEmptyResponsesChoice && (
                <EmptyResponsesChoice
                    isMultilabel={false}
                    taskType={selectedTaskType}
                    value={emptyMultilabelHandling}
                    onChange={setEmptyMultilabelHandling}
                    includeGenerateWithAi={false}
                />
            )}
            {selectionOnly && selectedDatasetId && (
                <div style={{ marginTop: '32px' }}>
                    {splitValidationError && (
                        <div style={{
                            color: '#ff4d4f',
                            fontSize: '14px',
                            marginBottom: '16px',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            {splitValidationError}
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="primary"
                            onClick={handleHuggingFacePick}
                            disabled={
                                splitsLoading ||
                                (!selectedTrainSplit && !selectedTestSplit)
                            }
                            startIcon={splitsLoading ? <Spinner size="small" /> : undefined}
                            aria-busy={splitsLoading}
                        >
                            Add
                        </Button>
                    </div>
                </div>
            )}
            {!selectionOnly && (isSummarization || extractedLabels.length > 0 || (labelError && labelError.includes('Please reduce label cardinality'))) && selectedLabelColumn && selectedTextColumn && (
                <div style={{ marginTop: '32px' }}>
                    {submissionError && (
                        <div style={{
                            color: '#ff4d4f',
                            fontSize: '12px',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            {submissionError}
                        </div>
                    )}
                    {splitValidationError && (
                        <div style={{
                            color: '#ff4d4f',
                            fontSize: '14px',
                            marginBottom: '16px',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            {splitValidationError}
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="primary"
                            onClick={handleHuggingFaceSubmit}
                            startIcon={uploading ? <Spinner size="small" /> : undefined}
                            disabled={uploading}
                            disableWhenReadOnly
                        >
                            {uploading ? 'Processing...' : 'Add'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HuggingFaceTab; 